/**
 * Wire protocol shared between the Code Lens UI (main thread) and the Python
 * Web Worker. This module is deliberately dependency-free (no React, no DOM,
 * no Pyodide) so both sides — and the worker entry point — can import it
 * without dragging the runtime into the main bundle.
 */

import type { CompletionItem } from "../autocomplete/completionProtocol";

/**
 * One parsed FIX message, structured for transfer into the worker. Unlike the
 * old flat `[tagNumber, value][]` form, `fields` is a nested dict keyed by
 * human-readable FIX field names (resolved on the TS side from the decoded
 * message structure), with repeating groups nested as lists of per-entry dicts.
 * Python turns the array of these into the `messages` list and the
 * `df_messages` DataFrame, so the structure is built once here.
 */
export type FixFieldValue = string | null | FixMessageFields[];

export type FixMessageFields = Record<string, FixFieldValue>;

export interface SerializedFixMessage {
  idx: number;
  /** SendingTime (52) if present, else best-effort message time. */
  timestamp: string | null;
  raw: string;
  /** Nested, FIX-name-keyed fields; group keys hold lists of entry dicts. */
  fields: FixMessageFields;
}

/**
 * A rendered result. One is produced per successful run; when user code calls
 * display() more than once, the values are wrapped in a `group` whose `items`
 * render in order.
 */
export type CodeOutput =
  | {
      kind: "dataframe";
      columns: string[];
      rows: unknown[][];
      truncated?: boolean;
      totalRows?: number;
    }
  | {
      kind: "group";
      items: CodeOutput[];
    }
  | {
      kind: "image";
      mime: "image/svg+xml" | "image/png";
      data: string;
    }
  | {
      kind: "text";
      stdout: string;
      stderr?: string;
      truncated?: boolean;
    }
  | {
      kind: "error";
      message: string;
      traceback?: string;
    }
  | {
      kind: "empty";
    };

export interface RuntimeError {
  message: string;
  traceback?: string;
}

export type PythonWorkerRequest =
  | { type: "init" }
  | {
      type: "run";
      requestId: string;
      code: string;
      messages: SerializedFixMessage[];
    }
  | {
      type: "complete";
      requestId: string;
      code: string;
      cursorOffset: number;
      messages: SerializedFixMessage[];
    }
  | { type: "dispose" };

export type PythonWorkerResponse =
  | { type: "ready" }
  | { type: "runResult"; requestId: string; output: CodeOutput }
  | {
      type: "completionResult";
      requestId: string;
      completions: CompletionItem[];
    }
  | { type: "error"; requestId?: string; error: RuntimeError }
  | { type: "log"; message: string };

/** MVP runtime limits. Enforced in the worker and surfaced in the UI. */
export const RUNTIME_LIMITS = {
  /** Messages beyond this are dropped before reaching Python. */
  maxInputMessages: 10_000,
  /** Rendered DataFrame rows are capped at this; the rest are summarised. */
  maxDataframeRows: 1_000,
  /** stdout/stderr captured beyond this many bytes is truncated. */
  maxStdoutBytes: 64 * 1024,
  /** Wall-clock budget for a single run before the worker is terminated. */
  executionTimeoutMs: 5_000,
} as const;
